/**
 * =============================================================================
 * Toolbar — B-6 shared primitive (ADR-025 §6.2; design-v3 §F)
 * =============================================================================
 *
 * Semantic layout wrapper for the repeated action rows (filter rows, bulk
 * bars, row-action clusters, pagination footers). EXTRACTED as a thin,
 * prop-faithful alias over BigDesign <Flex> — children stay verbatim at the
 * call sites (refs, handlers, testids untouched), and every prop maps 1:1 onto
 * the Flex prop the incumbent row already used, so migrated rows are
 * DOM-byte-identical. Presets exist so future rows reach for a named idiom
 * instead of re-deriving Flex prop combinations:
 *
 *   <Toolbar preset="filters">    ≡ Flex flexGap="1rem"    alignItems="flex-end" flexWrap="wrap"
 *   <Toolbar preset="actions">    ≡ Flex flexGap="0.75rem" alignItems="center"   flexWrap="wrap"
 *   <Toolbar preset="cluster">    ≡ Flex flexGap="0.5rem"  flexWrap="wrap"
 *   <Toolbar preset="pagination"> ≡ Flex justifyContent="space-between" alignItems="center"
 *
 * Explicit props override the preset (exactly what the incumbent used wins).
 * =============================================================================
 */

import React from 'react';
import { Flex } from '@bigcommerce/big-design';

type FlexProps = React.ComponentProps<typeof Flex>;

const PRESETS = {
  filters: { flexGap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' },
  actions: { flexGap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' },
  cluster: { flexGap: '0.5rem', flexWrap: 'wrap' },
  pagination: { justifyContent: 'space-between', alignItems: 'center' },
} satisfies Record<string, Partial<FlexProps>>;

export interface ToolbarProps extends Partial<FlexProps> {
  preset?: keyof typeof PRESETS;
  children?: React.ReactNode;
}

export function Toolbar({ preset, children, ...flexProps }: ToolbarProps): React.ReactElement {
  const base = preset ? PRESETS[preset] : {};
  return (
    <Flex {...base} {...flexProps}>
      {children}
    </Flex>
  );
}
